import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calculator,
  Car,
  ShieldCheck,
  Sparkles,
  Droplets,
  Layers,
  Plus,
  Check,
  ArrowRight,
  Loader2,
  CalendarDays,
} from "lucide-react";
import { format } from "date-fns";
import BookingPicker, { formatPreferred, slotLabel } from "@/components/BookingPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VEHICLE_SIZES = [
  { id: "small", label: "Small", desc: "Hatch / Coupe", multiplier: 0.85 },
  { id: "medium", label: "Medium", desc: "Sedan / Wagon", multiplier: 1 },
  { id: "large", label: "Large", desc: "SUV / Ute / 4x4", multiplier: 1.2 },
  { id: "xl", label: "XL", desc: "Van / 7-seater", multiplier: 1.4 },
];

const SERVICE_TIERS = [
  {
    id: "express",
    label: "Express Detail",
    desc: "Wash, decontamination & quick interior",
    base: [140, 200],
    iconKey: "Droplets",
  },
  {
    id: "comprehensive",
    label: "Comprehensive Detail",
    desc: "Full interior + exterior rejuvenation",
    base: [320, 480],
    iconKey: "Sparkles",
  },
  {
    id: "correction",
    label: "Paint Correction",
    desc: "Single-stage machine polish, swirl removal",
    base: [650, 1100],
    iconKey: "Layers",
  },
  {
    id: "ceramic",
    label: "Ceramic Coating",
    desc: "3-year hydrophobic premium coating",
    base: [590, 1190],
    iconKey: "ShieldCheck",
  },
];

const ICONS = { Sparkles, ShieldCheck, Droplets, Layers };

const ADDONS = [
  { id: "interior_deep", label: "Interior Deep Clean", price: 140 },
  { id: "engine_bay", label: "Engine Bay Detail", price: 95 },
  { id: "headlight", label: "Headlight Restoration", price: 110 },
  { id: "pet_hair", label: "Pet Hair Removal", price: 70 },
  { id: "leather", label: "Leather Conditioning", price: 80 },
];

const initialLead = { name: "", email: "", phone: "", notes: "" };

export default function Quote() {
  const [vehicle, setVehicle] = useState(VEHICLE_SIZES[1]);
  const [tier, setTier] = useState(SERVICE_TIERS[1]);
  const [addons, setAddons] = useState([]);
  const [prefDate, setPrefDate] = useState(null);
  const [prefSlot, setPrefSlot] = useState(null);
  const [lead, setLead] = useState(initialLead);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const [sectionVisible, setSectionVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    const s = sectionRef.current;
    const f = formRef.current;
    if (!s || !f) return;
    const so = new IntersectionObserver(
      ([e]) => setSectionVisible(e.isIntersecting),
      { threshold: 0.05 }
    );
    const fo = new IntersectionObserver(
      ([e]) => setFormVisible(e.isIntersecting),
      { threshold: 0.15 }
    );
    so.observe(s);
    fo.observe(f);
    return () => {
      so.disconnect();
      fo.disconnect();
    };
  }, []);

  const showMobileBar = sectionVisible && !formVisible;

  const toggleAddon = (id) =>
    setAddons((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const estimate = useMemo(() => {
    const [bLow, bHigh] = tier.base;
    const addonTotal = ADDONS.filter((a) => addons.includes(a.id)).reduce(
      (acc, a) => acc + a.price,
      0
    );
    const low = Math.round((bLow * vehicle.multiplier + addonTotal) / 5) * 5;
    const high = Math.round((bHigh * vehicle.multiplier + addonTotal) / 5) * 5;
    return { low, high };
  }, [vehicle, tier, addons]);

  const update = (key) => (e) =>
    setLead((l) => ({ ...l, [key]: e.target.value }));

  const valid =
    lead.name.trim() && lead.email.trim() && lead.phone.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please add your name, email and phone to receive the quote.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, {
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        vehicle_size: vehicle.label,
        service_tier: tier.label,
        addons: ADDONS.filter((a) => addons.includes(a.id)).map((a) => a.label),
        estimate_low: estimate.low,
        estimate_high: estimate.high,
        notes: lead.notes,
        preferred_date: prefDate ? format(prefDate, "yyyy-MM-dd") : null,
        preferred_slot: slotLabel(prefSlot),
      });
      toast.success("Quote locked in. Warren will confirm shortly.");
      setSent(true);
      setLead(initialLead);
      setPrefDate(null);
      setPrefSlot(null);
      setTimeout(() => setSent(false), 6000);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let detail = "Couldn't lock the quote. Please try again or call us.";
      if (typeof raw === "string") detail = raw;
      else if (Array.isArray(raw) && raw[0]?.msg) detail = raw[0].msg;
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const TierIcon = ICONS[tier.iconKey] || Sparkles;

  return (
    <section
      id="quote"
      ref={sectionRef}
      data-testid="quote-section"
      className="relative py-24 md:py-40 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/10 blur-[160px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7 reveal">
            <p className="eyebrow">04 — Instant Quote</p>
            <h2 className="font-display text-white font-light text-4xl md:text-6xl tracking-tighter mt-4 leading-[1.05]">
              Build your quote.{" "}
              <span className="gold-text italic">In seconds.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end reveal">
            <p className="text-white/55 text-base leading-relaxed">
              Pick your vehicle, choose a tier, add the extras you fancy.
              We’ll lock in a price range and have Warren confirm the
              specifics — usually within the day.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 reveal">
          {/* LEFT — selectors */}
          <div className="lg:col-span-7 space-y-6">
            {/* Vehicle */}
            <div className="border border-white/8 bg-[#0E0E0E] p-5 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Step 01 · Vehicle Size
                </p>
                <Car className="w-4 h-4 text-white/40" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {VEHICLE_SIZES.map((v) => {
                  const active = vehicle.id === v.id;
                  return (
                    <button
                      key={v.id}
                      data-testid={`quote-vehicle-${v.id}`}
                      onClick={() => setVehicle(v)}
                      className={`text-left p-4 border transition-all duration-300 ${
                        active
                          ? "border-[#D4AF37] bg-[#D4AF37]/8"
                          : "border-white/8 hover:border-white/30"
                      }`}
                    >
                      <p
                        className={`font-display text-lg ${
                          active ? "text-[#D4AF37]" : "text-white"
                        }`}
                      >
                        {v.label}
                      </p>
                      <p className="text-white/45 text-xs mt-1">{v.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tier */}
            <div className="border border-white/8 bg-[#0E0E0E] p-5 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Step 02 · Service Tier
                </p>
                <Sparkles className="w-4 h-4 text-white/40" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {SERVICE_TIERS.map((t) => {
                  const Icon = ICONS[t.iconKey] || Sparkles;
                  const active = tier.id === t.id;
                  return (
                    <button
                      key={t.id}
                      data-testid={`quote-tier-${t.id}`}
                      onClick={() => setTier(t)}
                      className={`text-left p-4 border transition-all duration-300 flex items-start gap-4 ${
                        active
                          ? "border-[#D4AF37] bg-[#D4AF37]/8"
                          : "border-white/8 hover:border-white/30"
                      }`}
                    >
                      <div
                        className={`w-9 h-9 border flex items-center justify-center shrink-0 ${
                          active
                            ? "border-[#D4AF37] text-[#D4AF37]"
                            : "border-white/20 text-white/60"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p
                          className={`font-display text-lg leading-tight ${
                            active ? "text-[#D4AF37]" : "text-white"
                          }`}
                        >
                          {t.label}
                        </p>
                        <p className="text-white/50 text-xs mt-1">
                          {t.desc}
                        </p>
                        <p className="text-white/35 text-[11px] mt-2 tracking-wide">
                          From ${t.base[0]} NZD · medium car
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Addons */}
            <div className="border border-white/8 bg-[#0E0E0E] p-5 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Step 03 · Add-ons (optional)
                </p>
                <Plus className="w-4 h-4 text-white/40" />
              </div>
              <div className="flex flex-wrap gap-2">
                {ADDONS.map((a) => {
                  const active = addons.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      data-testid={`quote-addon-${a.id}`}
                      onClick={() => toggleAddon(a.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 border text-xs uppercase tracking-[0.15em] transition-all ${
                        active
                          ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                          : "border-white/15 text-white/65 hover:border-[#D4AF37]/60 hover:text-white"
                      }`}
                    >
                      {active ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Plus className="w-3.5 h-3.5" />
                      )}
                      {a.label}
                      <span
                        className={`ml-1 ${
                          active ? "text-black/70" : "text-[#D4AF37]"
                        }`}
                      >
                        +${a.price}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Preferred time */}
            <BookingPicker
              date={prefDate}
              slot={prefSlot}
              onDateChange={setPrefDate}
              onSlotChange={setPrefSlot}
            />
          </div>

          {/* RIGHT — summary + lead */}
          <div className="lg:col-span-5">
            <form
              ref={formRef}
              data-testid="quote-form"
              onSubmit={submit}
              className="border border-[#D4AF37]/40 bg-gradient-to-b from-[#141008] to-[#0A0A0A] p-6 md:p-7 lg:sticky lg:top-28"
            >
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Your estimate
                </p>
                <Calculator className="w-4 h-4 text-[#D4AF37]" />
              </div>

              <div className="mt-5">
                <p className="text-white/55 text-xs uppercase tracking-[0.2em]">
                  Estimated range
                </p>
                <div
                  data-testid="quote-estimate"
                  className="font-display text-white font-light text-4xl md:text-5xl mt-2 tracking-tight"
                >
                  <span className="gold-text font-medium">
                    ${estimate.low}
                  </span>
                  <span className="text-white/40 mx-2">—</span>
                  <span className="gold-text font-medium">
                    ${estimate.high}
                  </span>
                  <span className="text-white/40 text-base ml-2 align-middle">
                    NZD
                  </span>
                </div>
                <p className="text-white/40 text-xs mt-2">
                  Final price confirmed after a quick vehicle inspection.
                </p>
              </div>

              <div className="mt-6 space-y-2 border-t border-white/8 pt-5">
                <SummaryRow label="Vehicle" value={vehicle.label} />
                <SummaryRow label="Service" value={tier.label} icon={TierIcon} />
                <SummaryRow
                  label="Add-ons"
                  value={
                    addons.length === 0
                      ? "None"
                      : `${addons.length} selected`
                  }
                />
                <SummaryRow
                  label="Preferred"
                  value={formatPreferred(prefDate, prefSlot)}
                  icon={CalendarDays}
                  testid="quote-summary-preferred"
                />
              </div>

              <div className="mt-6 grid grid-cols-1 gap-3">
                <input
                  data-testid="quote-input-name"
                  value={lead.name}
                  onChange={update("name")}
                  className="input-dark w-full px-4 py-3 text-sm"
                  placeholder="Your name *"
                  required
                />
                <input
                  data-testid="quote-input-email"
                  type="email"
                  value={lead.email}
                  onChange={update("email")}
                  className="input-dark w-full px-4 py-3 text-sm"
                  placeholder="Email *"
                  required
                />
                <input
                  data-testid="quote-input-phone"
                  type="tel"
                  value={lead.phone}
                  onChange={update("phone")}
                  className="input-dark w-full px-4 py-3 text-sm"
                  placeholder="Phone *"
                  required
                />
                <textarea
                  data-testid="quote-input-notes"
                  value={lead.notes}
                  onChange={update("notes")}
                  rows={2}
                  className="input-dark w-full px-4 py-3 text-sm resize-none"
                  placeholder="Make / model / anything we should know"
                />
              </div>

              <button
                data-testid="quote-submit"
                type="submit"
                disabled={submitting}
                className="btn-gold w-full px-6 py-4 text-xs flex items-center justify-center gap-3 mt-5 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Locking...
                  </>
                ) : sent ? (
                  <>
                    <Check className="w-4 h-4" /> Quote Sent
                  </>
                ) : (
                  <>
                    Lock in this Quote{" "}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-white/35 text-[11px] mt-3 leading-relaxed">
                We’ll only use your details to confirm this quote — no
                spam, ever.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Mobile floating estimate bar */}
      <div
        data-testid="quote-mobile-bar"
        aria-hidden={!showMobileBar}
        className={`lg:hidden fixed inset-x-0 bottom-0 z-40 transition-all duration-400 ${
          showMobileBar
            ? "translate-y-0 opacity-100"
            : "translate-y-full opacity-0 pointer-events-none"
        }`}
      >
        <div className="mx-3 mb-3 border border-[#D4AF37]/50 bg-[#0C0A05]/95 backdrop-blur-xl shadow-[0_-10px_40px_rgba(0,0,0,0.6)] px-4 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">
              Your estimate
            </p>
            <p
              data-testid="quote-mobile-estimate"
              className="font-display text-white text-2xl leading-none mt-1 tracking-tight"
            >
              <span className="gold-text font-medium">${estimate.low}</span>
              <span className="text-white/40 mx-1.5">—</span>
              <span className="gold-text font-medium">${estimate.high}</span>
            </p>
            <p className="text-white/45 text-[11px] mt-1 truncate">
              {vehicle.label} · {tier.label}
              {addons.length > 0 && ` · +${addons.length} add-on${addons.length > 1 ? "s" : ""}`}
            </p>
          </div>
          <button
            type="button"
            data-testid="quote-mobile-lock"
            onClick={() =>
              formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            className="btn-gold px-4 py-3 text-[11px] flex items-center gap-2 shrink-0"
          >
            Lock in <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </section>
  );
}

function SummaryRow({ label, value, icon: Icon, testid }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/45 uppercase tracking-[0.2em] text-[10px]">
        {label}
      </span>
      <span data-testid={testid} className="text-white flex items-center gap-2 font-display">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />}
        {value}
      </span>
    </div>
  );
}
